import React, { useEffect, useRef, useState } from 'react';
import Dropdown from './Dropdown';
import { CONTROL_BOX } from '../../constants/inputs';
import { addDays, parseDate, toDateStr, toMonday } from '../../utils/ganttDates';
import { formatDate, formatDateDayMonth, toLocalISO } from '../../utils/dateFormat';

/**
 * THE week picker — ONE box, `‹ w/c 19 Oct 2026 ›`, in the toolbar's box style
 * (`CONTROL_BOX`, the search field's own height, border and fill). Owner,
 * 2026-10-25 (Timesheets, approved from a live mock): one box, the date input
 * hidden behind the middle, no separate "w/c" label beside it.
 *
 * - `‹` / `›` step a week; the middle opens the browser's date picker (an
 *   invisible `<input type="date">` laid over the label) and whatever day is
 *   picked snaps to its Monday.
 * - CONTROLLED: `weekStart` is a Monday `YYYY-MM-DD`; `onChange(monday)` only
 *   PROPOSES a week — the caller owns it and gates the change (a dirty-edit
 *   prompt), exactly as `components/timesheets/WeekPicker.js` did before it
 *   was replaced by this.
 * - 📱 Below `md` each arrow is a 44px target (`CONTROL_HEIGHT` carries the
 *   height).
 * - Test ids: `${idPrefix}-picker` (the box), `-prev`, `-next`, `-date`, `-label`.
 * - 📅 THE MONDAY CALENDAR (owner 2026-11-01: "next to the date/week selector please include a
 *   date picker that allows selecting mondays on a calendar"): a button BESIDE the box (`-cal`)
 *   opens a month on `ui/Dropdown` (a bottom sheet below `md`). ONLY Mondays are buttons
 *   (`-cal-day-YYYY-MM-DD`); hovering one shades its week; the shown week is marked
 *   (`aria-pressed`); ‹ › change month (`-cal-prev-month` / `-cal-next-month`); `-cal-today`
 *   proposes this week. A pick calls the SAME `onChange` the arrows call — the caller's
 *   dirty-guard runs on it — then closes and focus returns to the button. Keys: Tab / Enter on a
 *   Monday, ↑ ↓ step a week (crossing months), Esc closes. Esc is stopped at the panel so a
 *   picker inside a `ConfirmDialog` closes the calendar, never the dialog. Dates are built from
 *   Y-M-D parts (`parseDate` / `toLocalISO`), never UTC-parsed.
 *
 * Knows nothing about timesheets. It is the only week picker in the app
 * (grep, 2026-10-25); a new one is THIS component, never a copy.
 */

/** The Monday (YYYY-MM-DD) of the week holding `ymd`. */
/** THE week step: the Monday `weeks` weeks from `weekStart` (the picker arrows and the phone end cards). */
export function shiftWeek(weekStart, weeks) {
  return toDateStr(addDays(parseDate(weekStart), weeks * 7));
}

export function mondayOfDate(ymd) {
  const d = parseDate(ymd);
  return d ? toDateStr(toMonday(d)) : null;
}

const ARROW =
  'inline-flex items-center justify-center min-w-[44px] md:min-w-[1.75rem] px-1 text-lg leading-none ' +
  'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';

// The compact look: no box, blue arrows, bold date (the phone header mock B).
const ARROW_COMPACT =
  'inline-flex items-center justify-center min-w-[40px] min-h-[40px] text-xl leading-none rounded-full ' +
  'text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';

// The 📅 trigger: the box's own look and height; a 44px square below `md` (the phone tap target).
const CAL_TRIGGER =
  `${CONTROL_BOX} inline-flex shrink-0 items-center justify-center min-w-[44px] md:min-w-0 md:w-[2.375rem] ` +
  'hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';
const CAL_TRIGGER_COMPACT =
  'inline-flex shrink-0 items-center justify-center min-w-[44px] min-h-[44px] rounded-full text-lg leading-none ' +
  'hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';
const CAL_NAV =
  'inline-flex items-center justify-center min-w-[44px] min-h-[44px] md:min-w-[2rem] md:min-h-[2rem] rounded-md text-lg leading-none ' +
  'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';
const CAL_CELL = 'flex items-center justify-center h-10 md:h-8 text-sm tabular-nums';
const CAL_MONDAY =
  `${CAL_CELL} w-full rounded-md font-semibold ring-1 ring-inset ring-gray-300 dark:ring-gray-500 ` +
  'text-gray-900 dark:text-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';
const CAL_MONDAY_ON = 'ring-2 ring-blue-600 dark:ring-blue-400 text-blue-800 dark:text-blue-200';
const WEEKDAY_HEADINGS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** The Mondays whose week touches the month holding `ymd` (5 or 6 rows), as YYYY-MM-DD. */
export function monthWeekStarts(ymd) {
  const d = parseDate(ymd);
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const out = [];
  for (let w = toMonday(first); w <= last; w = addDays(w, 7)) out.push(toDateStr(w));
  return out;
}

/** The first of the month `n` months from the month holding `ymd`. */
function shiftMonth(ymd, n) {
  const d = parseDate(ymd);
  return toDateStr(new Date(d.getFullYear(), d.getMonth() + n, 1));
}

function MonthCalendar({ weekStart, onPick, onClose, idPrefix, maxWeekStart = null }) {
  const [view, setView] = useState(() => shiftMonth(weekStart, 0));
  const [hover, setHover] = useState(null);
  const [focusDay, setFocusDay] = useState(weekStart);
  const panelRef = useRef(null);
  const viewDate = parseDate(view);
  const viewMonth = viewDate.getMonth();
  const rows = monthWeekStarts(view);

  // Focus follows `focusDay` (the shown week on open; ↑ ↓ thereafter).
  useEffect(() => {
    const el = panelRef.current && panelRef.current.querySelector(`[data-testid="${idPrefix}-cal-day-${focusDay}"]`);
    if (el) el.focus();
  }, [focusDay, view, idPrefix]);

  const step = (monday, weeks) => {
    const next = shiftWeek(monday, weeks);
    if (!rows.includes(next)) setView(shiftMonth(next, 0));
    setFocusDay(next);
  };
  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      // Stopped here so a picker inside a ConfirmDialog closes the CALENDAR, not the dialog.
      // (React's stopPropagation stops the NATIVE event too — Dropdown's document listener included.)
      e.stopPropagation();
      onClose();
      return;
    }
    const monday = e.target && e.target.getAttribute && e.target.getAttribute('data-monday');
    if (!monday) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      step(monday, e.key === 'ArrowDown' ? 1 : -1);
    }
  };
  const monthLabel = viewDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  return (
    <div ref={panelRef} onKeyDown={onKeyDown} data-testid={`${idPrefix}-cal-panel`} className="text-gray-800 dark:text-gray-100">
      <div className="mb-1 flex items-center justify-between">
        <button type="button" className={CAL_NAV} aria-label="Previous month" data-testid={`${idPrefix}-cal-prev-month`}
          onClick={() => setView(shiftMonth(view, -1))}>‹</button>
        <span className="text-sm font-semibold" aria-live="polite" data-testid={`${idPrefix}-cal-month`}>{monthLabel}</span>
        <button type="button" className={CAL_NAV} aria-label="Next month" data-testid={`${idPrefix}-cal-next-month`}
          onClick={() => setView(shiftMonth(view, 1))}>›</button>
      </div>
      <div className="grid grid-cols-7 gap-0.5" aria-hidden="true">
        {WEEKDAY_HEADINGS.map((d) => (
          <span key={d} className={`text-center text-[11px] ${d === 'Mon' ? 'font-semibold text-gray-700 dark:text-gray-200' : 'text-gray-500 dark:text-gray-400'}`}>{d}</span>
        ))}
      </div>
      <div role="group" aria-label={`Weeks in ${monthLabel}`} className="mt-0.5 space-y-0.5">
        {rows.map((monday) => {
          const on = monday === weekStart;
          const shaded = on || hover === monday;
          const shade = on ? 'bg-blue-100 dark:bg-blue-900/50' : shaded ? 'bg-blue-50 dark:bg-blue-900/25' : '';
          return (
            <div key={monday} data-testid={`${idPrefix}-cal-week-${monday}`} data-selected={on ? 'true' : undefined}
              data-hover={hover === monday ? 'true' : undefined}
              className={`grid grid-cols-7 gap-0.5 rounded-md ${shade}`}>
              {[0, 1, 2, 3, 4, 5, 6].map((i) => {
                const day = addDays(parseDate(monday), i);
                const ymd = toDateStr(day);
                const out = day.getMonth() !== viewMonth;
                if (i === 0) {
                  const closed = beyond(ymd, maxWeekStart);
                  return (
                    <button key={ymd} type="button" data-monday={ymd} data-testid={`${idPrefix}-cal-day-${ymd}`}
                      aria-pressed={on} aria-label={`Week commencing ${formatDate(ymd)}${closed ? ' — not open yet' : ''}`}
                      title={closed ? 'Not open yet — you can fill in up to next week' : `w/c ${formatDate(ymd)}`}
                      tabIndex={ymd === focusDay ? 0 : -1}
                      disabled={closed}
                      className={`${CAL_MONDAY} ${on ? CAL_MONDAY_ON : ''} ${closed ? 'opacity-40 cursor-not-allowed' : out ? 'opacity-60' : ''}`}
                      onMouseEnter={() => setHover(ymd)} onMouseLeave={() => setHover(null)}
                      onFocus={() => setHover(ymd)} onBlur={() => setHover(null)}
                      onClick={() => onPick(ymd)}>
                      {day.getDate()}
                    </button>
                  );
                }
                return (
                  <span key={ymd} aria-hidden="true"
                    className={`${CAL_CELL} ${out ? 'text-gray-300 dark:text-gray-600' : 'text-gray-500 dark:text-gray-400'}`}>
                    {day.getDate()}
                  </span>
                );
              })}
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex items-center justify-between border-t border-gray-200 pt-1.5 dark:border-gray-700">
        <button type="button" data-testid={`${idPrefix}-cal-today`}
          className="min-h-[44px] md:min-h-0 rounded px-1 py-1 text-sm font-medium text-blue-700 hover:underline dark:text-blue-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          onClick={() => onPick(mondayOfDate(toLocalISO(new Date())))}>This week</button>
        <span className="text-xs text-gray-500 dark:text-gray-400">Pick a Monday</span>
      </div>
    </div>
  );
}

/** True when `monday` is after the latest pickable week (`max` null = no limit). */
const beyond = (monday, max) => Boolean(max) && monday > max;

/** The 📅 button + its month. `onChange` is the picker's own (the caller's guarded week change). */
function WeekCalendarButton({ weekStart, onChange, idPrefix, compact, maxWeekStart = null }) {
  return (
    <Dropdown
      haspopup="dialog"
      hideChevron
      align={compact ? 'right' : 'left'}
      panelClassName="w-72 p-2"
      triggerClassName={compact ? CAL_TRIGGER_COMPACT : CAL_TRIGGER}
      title="Pick a week on a calendar"
      ariaLabel="Pick a week on a calendar"
      triggerTestId={`${idPrefix}-cal`}
      label={<span aria-hidden="true">📅</span>}
    >
      {({ close }) => (
        <MonthCalendar weekStart={weekStart} idPrefix={idPrefix} onClose={close} maxWeekStart={maxWeekStart}
          onPick={(monday) => { if (beyond(monday, maxWeekStart)) return; close(); onChange(monday); }} />
      )}
    </Dropdown>
  );
}

// compact (opt-in, the timesheet phone header 2026-10-27): the label reads '26 Oct', no 'w/c' or year.
// maxWeekStart (opt-in, My timesheet 2026-11-01, owner ruling A): the latest pickable Monday — › is
// disabled on it, later Mondays are greyed and disabled in the calendar, the date box stops at its
// Sunday. Omitted = no limit, byte-identical for every other caller. The SERVER enforces it too.
export default function WeekPicker({ weekStart, onChange, idPrefix = 'week', ariaLabel = 'Week', compact = false, maxWeekStart = null }) {
  const shift = (weeks) => shiftWeek(weekStart, weeks);
  const nextClosed = beyond(shift(1), maxWeekStart);
  return (
    <div className="inline-flex shrink-0 items-center gap-1">
    <div
      role="group"
      aria-label={ariaLabel}
      data-testid={`${idPrefix}-picker`}
      className={compact ? 'inline-flex shrink-0 items-center' : `inline-flex shrink-0 items-stretch overflow-hidden ${CONTROL_BOX}`}
    >
      <button
        type="button"
        aria-label="Previous week"
        data-testid={`${idPrefix}-prev`}
        className={compact ? ARROW_COMPACT : `${ARROW} rounded-l-lg`}
        onClick={() => onChange(shift(-1))}
      >
        ‹
      </button>
      <label className={`relative flex items-center whitespace-nowrap ${compact ? 'px-0.5 text-[15px] font-semibold text-gray-900 dark:text-gray-100' : 'px-1.5'} cursor-pointer tabular-nums focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-500`}>
        <span data-testid={`${idPrefix}-label`}>{compact ? formatDateDayMonth(weekStart) : `w/c ${formatDate(weekStart)}`}</span>
        <input
          type="date"
          aria-label="Week commencing — pick any day"
          data-testid={`${idPrefix}-date`}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          value={weekStart}
          max={maxWeekStart ? toDateStr(addDays(parseDate(maxWeekStart), 6)) : undefined}
          onClick={(e) => { if (typeof e.currentTarget.showPicker === 'function') { try { e.currentTarget.showPicker(); } catch (_) { /* not user-activated */ } } }}
          onChange={(e) => { const m = e.target.value ? mondayOfDate(e.target.value) : null; if (m && !beyond(m, maxWeekStart)) onChange(m); }}
        />
      </label>
      <button
        type="button"
        aria-label="Next week"
        data-testid={`${idPrefix}-next`}
        className={`${compact ? ARROW_COMPACT : `${ARROW} rounded-r-lg`}${nextClosed ? ' opacity-40 cursor-not-allowed' : ''}`}
        disabled={nextClosed}
        title={nextClosed ? 'You can fill in up to next week' : undefined}
        onClick={() => { if (!nextClosed) onChange(shift(1)); }}
      >
        ›
      </button>
    </div>
    <WeekCalendarButton weekStart={weekStart} onChange={onChange} idPrefix={idPrefix} compact={compact} maxWeekStart={maxWeekStart} />
    </div>
  );
}
